<template>
  <Teleport to="body">
  <div
    v-if="open"
    class="bulk-import-overlay"
    @click.self="onClose"
  >
    <div class="bulk-import-card" @click.stop>
      <div class="flex items-start justify-between gap-3 mb-1">
        <h3 class="text-base font-semibold">
          <i class="mr-1 fa fa-edit"></i> Importo kode për ndryshim në grup
        </h3>
        <button type="button" class="text-slate-500 hover:text-slate-800" :disabled="busy" @click="onClose" title="Mbyll">
          <i class="fa fa-times"></i>
        </button>
      </div>
      <p class="mb-4 text-sm text-slate-600">
        Shkarkoni shabllonin, plotësoni vlerat e reja (qeliza bosh = pa ndryshim) dhe ngarkoni skedarin.
        Kodi identifikon produktin dhe nuk ndryshohet.
        Nëse Delisto = 1 (Po) pa koment, rreshti anashkalohet dhe produkti nuk delistohet.
      </p>

      <div class="flex flex-wrap items-center gap-2 mb-3">
        <button type="button" class="px-3 py-1 text-sm border rounded" :disabled="busy" @click="downloadTemplate">
          <i class="mr-1 fa fa-download"></i> Shkarko shabllonin
        </button>
        <label class="px-3 py-1 text-sm border rounded cursor-pointer" :class="{ 'opacity-50 pointer-events-none': busy }">
          <i class="mr-1 fa fa-file-excel"></i> Zgjidh skedar
          <input
            ref="fileInput"
            type="file"
            class="hidden"
            accept=".csv,.xlsx,.xls"
            :disabled="busy"
            @change="onFilePicked"
          />
        </label>
        <span class="text-xs text-slate-500">{{ fileName || "CSV, XLSX, XLS · maks. 2000 rreshta" }}</span>
      </div>

      <div v-if="loading" class="py-6 text-sm text-center text-slate-500">
        <i class="mr-1 fa fa-spinner fa-spin"></i> Duke validuar skedarin...
      </div>
      <div v-else-if="applying" class="py-6 text-sm text-center text-slate-500">
        <i class="mr-1 fa fa-spinner fa-spin"></i> Duke aplikuar ndryshimet...
      </div>

      <div v-if="error" class="p-3 mb-3 text-sm text-red-700 rounded bg-red-50">{{ error }}</div>

      <div v-if="preview" class="mb-3 space-y-2">
        <div class="flex flex-wrap gap-2 text-xs">
          <span class="px-2 py-1 rounded bg-green-50 text-green-800">OK: {{ preview.valid_count || 0 }}</span>
          <span class="px-2 py-1 rounded bg-slate-100 text-slate-700">Pa ndryshim: {{ preview.skip_count || 0 }}</span>
          <span class="px-2 py-1 rounded" :class="skippedRows.length ? 'bg-orange-50 text-orange-800' : 'bg-slate-100 text-slate-500'">
            Anashkaluar: {{ skippedRows.length }}
          </span>
          <span class="px-2 py-1 rounded" :class="(preview.warning_count || 0) ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-500'">
            Warning: {{ preview.warning_count || 0 }}
          </span>
          <span class="px-2 py-1 rounded" :class="(preview.error_count || 0) ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-500'">
            Gabime: {{ preview.error_count || 0 }}
          </span>
        </div>
        <div class="max-h-80 overflow-auto border rounded">
          <table class="w-full text-xs">
            <thead class="sticky top-0 bg-slate-50">
              <tr>
                <th class="p-2 text-left">Rreshti</th>
                <th class="p-2 text-left">Kodi</th>
                <th class="p-2 text-left">Produkti</th>
                <th class="p-2 text-left">Ndryshimet</th>
                <th class="p-2 text-left">Statusi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in preview.rows" :key="row.line + '-' + row.code" :class="rowClass(row)">
                <td class="p-2 align-top">{{ row.line }}</td>
                <td class="p-2 align-top font-mono">{{ row.code || "—" }}</td>
                <td class="p-2 align-top">{{ row.name || "—" }}</td>
                <td class="p-2 align-top">{{ (row.change_labels || []).join("; ") || "—" }}</td>
                <td class="p-2 align-top">
                  <span v-if="row.error" class="text-red-700">{{ row.error }}</span>
                  <span v-else-if="row.skip_reason" class="text-orange-800">{{ row.skip_reason }}</span>
                  <span v-else-if="(row.warnings || []).length" class="text-amber-700">
                    {{ row.change_labels?.length ? "OK. " : "Pa ndryshim. " }}{{ row.warnings.join(" ") }}
                  </span>
                  <span v-else-if="row.change_labels?.length" class="text-green-700">OK</span>
                  <span v-else class="text-slate-500">Pa ndryshim</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="skippedRows.length" class="mb-3 border border-orange-200 rounded">
        <div class="px-3 py-2 text-sm font-semibold text-orange-900 bg-orange-50">
          Rreshtat e anashkaluar ({{ skippedRows.length }})
        </div>
        <div class="max-h-56 overflow-auto">
          <table class="w-full text-xs">
            <thead class="sticky top-0 bg-white">
              <tr>
                <th class="p-2 text-left">Rreshti</th>
                <th class="p-2 text-left">Kodi</th>
                <th class="p-2 text-left">Produkti</th>
                <th class="p-2 text-left">Arsyeja</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in skippedRows" :key="'skip-' + row.line + '-' + row.code" class="bg-orange-50/60">
                <td class="p-2 align-top">{{ row.line }}</td>
                <td class="p-2 align-top font-mono">{{ row.code || "—" }}</td>
                <td class="p-2 align-top">{{ row.name || "—" }}</td>
                <td class="p-2 align-top text-orange-800">{{ row.reason || row.skip_reason }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="applyResult" class="p-3 mb-3 text-sm rounded bg-slate-50 border">
        U përditësuan {{ applyResult.updated }} produkte.
        {{ applyResult.skip_count || 0 }} pa ndryshim.
        {{ skippedRows.length }} u anashkaluan.
        {{ applyResult.failed || 0 }} dështuan.
        <div class="mt-1">
          Financa: {{ applyResult.financa_synced || 0 }} u ruajtën,
          {{ applyResult.financa_failed || 0 }} dështuan<span v-if="applyResult.financa_skipped">, {{ applyResult.financa_skipped }} nuk u gjetën</span>.
        </div>
        <div v-if="financaFailures.length" class="mt-1 text-red-700">
          {{ financaFailureDetail || "Nuk u ruajtën në Financa" }}: {{ financaFailures.join(", ") }}
        </div>
      </div>

      <div class="flex flex-wrap justify-end gap-2 mt-4">
        <button
          v-if="hasErrors"
          type="button"
          class="px-3 py-1 text-sm border border-red-200 text-red-700 rounded"
          :disabled="busy"
          @click="downloadErrors"
        >
          Shkarko gabimet
        </button>
        <button type="button" class="px-3 py-1 text-sm border rounded" :disabled="busy" @click="onClose">
          {{ applyResult ? "Mbyll" : "Anulo" }}
        </button>
        <button
          type="button"
          class="px-3 py-1 text-sm text-white bg-teal-600 rounded"
          :class="{ 'opacity-50 pointer-events-none': busy || !canApply }"
          @click="applyImport"
        >
          Apliko{{ preview?.valid_count ? ` (${preview.valid_count})` : "" }}
        </button>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import ProductsServices from "@/services/Product";
import { financaBulkFailureText } from "@/utils/financaSaveNotice";

export default {
  name: "ProductBulkImportModal",
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ["close", "applied"],
  data() {
    return {
      file: null,
      fileName: "",
      loading: false,
      applying: false,
      error: null,
      preview: null,
      applyResult: null,
    };
  },
  computed: {
    busy() {
      return this.loading || this.applying;
    },
    canApply() {
      return !!this.file && (this.preview?.valid_count || 0) > 0 && !this.applyResult;
    },
    hasErrors() {
      return (this.preview?.error_count || 0) > 0;
    },
    financaFailureDetail() {
      return financaBulkFailureText(this.applyResult?.financa_failed_sync);
    },
    financaFailures() {
      const rows = this.applyResult?.financa_failed_sync || [];
      const codes = [];
      rows.forEach((row) => {
        const code = row?.code || row?.reason;
        if (code && !codes.includes(code)) codes.push(code);
      });
      return codes;
    },
    skippedRows() {
      if (this.applyResult?.skipped_rows?.length) return this.applyResult.skipped_rows;
      if (this.preview?.skipped_rows?.length) return this.preview.skipped_rows;
      return (this.preview?.rows || []).filter((r) => r.skip_reason).map((r) => ({
        line: r.line,
        code: r.code,
        name: r.name,
        reason: r.skip_reason,
      }));
    },
  },
  watch: {
    open(value) {
      if (value) this.reset();
    },
  },
  methods: {
    reset() {
      this.file = null;
      this.fileName = "";
      this.loading = false;
      this.applying = false;
      this.error = null;
      this.preview = null;
      this.applyResult = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    onClose() {
      if (this.busy) return;
      this.$emit("close");
    },
    rowClass(row) {
      if (row.error) return "bg-red-50";
      if (row.skip_reason) return "bg-orange-50";
      if ((row.warnings || []).length) return "bg-amber-50";
      if (row.change_labels?.length) return "";
      return "text-slate-500";
    },
    async downloadTemplate() {
      try {
        await ProductsServices.downloadBulkImportTemplate();
      } catch (e) {
        this.error = e?.response?.data?.message || e?.message || "Nuk u shkarkua shablloni.";
      }
    },
    async onFilePicked(event) {
      const file = event?.target?.files?.[0];
      if (event?.target) event.target.value = null;
      if (!file) return;
      this.file = file;
      this.fileName = file.name;
      this.error = null;
      this.preview = null;
      this.applyResult = null;
      this.loading = true;
      try {
        const data = await ProductsServices.previewBulkImport(file);
        this.preview = data;
        if (!data?.rows?.length) {
          this.error = "Skedari nuk ka rreshta të vlefshëm.";
        }
      } catch (e) {
        this.error = e?.response?.data?.message || e?.message || "Gabim gjatë leximit të skedarit.";
        this.preview = null;
      } finally {
        this.loading = false;
      }
    },
    async applyImport() {
      if (!this.canApply) return;
      this.applying = true;
      this.error = null;
      try {
        const data = await ProductsServices.applyBulkImport(this.file);
        this.applyResult = data;
        this.preview = {
          ...this.preview,
          rows: data.rows || this.preview.rows,
          skipped_rows: data.skipped_rows || this.preview.skipped_rows,
          valid_count: data.valid_count,
          error_count: data.error_count,
          warning_count: data.warning_count,
          skip_count: data.skip_count,
          skipped_reason_count: data.skipped_reason_count,
        };
        this.$emit("applied", data);
      } catch (e) {
        this.error = e?.response?.data?.message || e?.message || "Gabim gjatë aplikimit.";
      } finally {
        this.applying = false;
      }
    },
    downloadErrors() {
      const failed = (this.preview?.rows || []).filter((r) => r.error);
      const payload = {
        generated_at: new Date().toISOString(),
        error_count: failed.length,
        rows: failed.map((r) => ({
          line: r.line,
          code: r.code,
          error: r.error,
          warnings: r.warnings || [],
        })),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "produkte_import_gabime.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.bulk-import-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  overflow-y: auto;
  background: rgba(10, 10, 10, 0.52);
}
.bulk-import-card {
  width: 100%;
  max-width: 64rem;
  max-height: calc(100vh - 3rem);
  overflow-y: auto;
  margin: auto;
  padding: 1rem;
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}
</style>
